import React, { useState } from 'react';
import { Button, Input, Text, Icon, Radio } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { PlatformType } from '../../types/project';


interface CloneProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  userInput: {
    title?: string;
    clientName?: string;
    projectType?: number;
    platformType?: PlatformType;
  };
  setUserInput: (v: {
    title?: string;
    clientName?: string;
    projectType?: number;
    platformType?: PlatformType;
  }) => void;
  sourceProjectType?: number;
  sourcePlatformType?: PlatformType;
}

export const CloneProjectModal: React.FC<CloneProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  userInput,
  setUserInput,
  sourceProjectType = 1,
  sourcePlatformType,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Clone Project</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto">
            <div className="w-full space-y-6">
              <Input
                id="project-name-clone"
                name="project-name-clone"
                label="Project Name"
                value={userInput.title || ''}
                onChange={e => setUserInput({ ...userInput, title: e.target.value })}
                required
                autoFocus
                placeholder="Enter project name"
                className="w-full"
              />

              <div className="h-[1px] bg-border-secondary w-full"></div>

              <div className="w-full">
                <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Project Type</Text>
                <div className="space-y-2">
                  <Radio
                    name="cloneProjectType"
                    value={1}
                    checked={(userInput.projectType ?? sourceProjectType) === 1}
                    onChange={() => setUserInput({ ...userInput, projectType: 1 })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Web Application</Text>}
                    size='sm'
                  />
                  <Radio
                    name="cloneProjectType"
                    value={2}
                    checked={(userInput.projectType ?? sourceProjectType) === 2}
                    onChange={() => setUserInput({ ...userInput, projectType: 2 })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Low Code Application</Text>}
                    size='sm'
                  />
                </div>
              </div>

              {(userInput.projectType ?? sourceProjectType) === 2 ? (
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.08s', animationFillMode: 'forwards' }}>
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Platform Type</Text>
                  <div className="space-y-2">
                    <Radio
                      name="clonePlatformType"
                      value={1}
                      checked={(userInput.platformType ?? sourcePlatformType) === PlatformType.TylerAppPlatform}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.TylerAppPlatform })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Tyler Application Platform (TAP)</Text>}
                      size='sm'
                    />
                    <Radio
                      name="clonePlatformType"
                      value={2}
                      checked={(userInput.platformType ?? sourcePlatformType) === PlatformType.ServiceNow}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.ServiceNow })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">ServiceNow (SN)</Text>}
                      size='sm'
                    />
                    <Radio
                      name="clonePlatformType"
                      value={3}
                      checked={(userInput.platformType ?? sourcePlatformType) === PlatformType.PowerApps}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.PowerApps })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">PowerApps (PA)</Text>}
                      size='sm'
                    />
                    <Radio
                      name="clonePlatformType"
                      value={4}
                      checked={(userInput.platformType ?? sourcePlatformType) === PlatformType.Salesforce}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.Salesforce })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Salesforce (SF)</Text>}
                      size='sm'
                    />
                  </div>
                </div>
              ) : (
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.08s', animationFillMode: 'forwards' }}>
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Platform Type</Text>
                  <div className="space-y-2">
                    <Radio
                      name="clonePlatformTypeWeb"
                      value="ReactNodePostgreSQL"
                      checked={true}
                      onChange={() => {}}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Made with React, Node.js, and PostgreSQL</Text>}
                      size='sm'
                    />
                  </div>
                </div>
              )}

              <Input
                id="client-name-clone"
                name="client-name-clone"
                label="Client Name"
                value={userInput.clientName || ''}
                onChange={e => setUserInput({ ...userInput, clientName: e.target.value })}
                placeholder="Enter client name"
                className="w-full"
              />
            </div>
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Cloning...' : 'Clone Project'}
              disabled={loading || !userInput.title?.trim()}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

